import { useEffect, useRef, useState } from 'react'
import type Hls from 'hls.js'
import { API_URL, ApiError, api } from '../../shared/api/http'
import type { LiveAccess } from './monitoringApi'

type Props = {
  cameraId: string
  name: string
  active: boolean
  onDimensions?: (width: number, height: number) => void
}

type PlayerState = 'connecting' | 'playing' | 'waiting' | 'offline' | 'stopped' | 'unavailable'
type Snapshot = {
  atUtc: string
  source: 'hls'
  state: PlayerState
  phase: string
  attempt: number
  reconnects: number
  authRenewals: number
  currentTime: number
  videoFrames: number
  framesDecoded: number
  framesDropped: number
  presentedFrames: number
  fps: number
  presentedFps: number
  bufferedSeconds: number
  latencySeconds: number | null
  firstFrameMs: number | null
  frameAgeMs: number | null
  stalls: number
  stalledMs: number
  readyState: number
  paused: boolean
  visibility: DocumentVisibilityState
  onScreen: boolean
}

// A preview ticket can be returned in either pair of fields by the API.
// Accept only the recordings HLS gateway, never an arbitrary media host.
function resolveHlsAccess(access: LiveAccess) {
  const apiBase = new URL(API_URL, window.location.origin)
  const gateway = `${apiBase.pathname.replace(/\/$/, '')}/recordings/live/`
  for (const [candidate, token] of [[access.previewUrl, access.previewAccessToken], [access.whepUrl, access.accessToken]]) {
    if (!candidate || !token) continue
    let url: URL
    try { url = new URL(candidate, `${apiBase.href}/`) } catch { continue }
    const resource = url.pathname.slice(gateway.length)
    if (url.origin !== apiBase.origin || !url.pathname.startsWith(gateway) ||
      url.username || url.password || url.search || url.hash ||
      !/^[0-9a-f-]{36}\/hls\/index\.m3u8$/i.test(resource)) continue
    if (import.meta.env.DEV && !import.meta.env.VITE_API_URL) {
      url = new URL(`/backend-api${url.pathname}`, window.location.origin)
    }
    return { url: url.href, token, origin: url.origin, prefix: url.pathname.slice(0, -'index.m3u8'.length) }
  }
  throw new Error('hls_access_unavailable')
}

const retryDelays = [500, 1500, 3000, 6000, 12000, 20000]
const rounded = (value: number) => Math.round(value * 100) / 100

export function HlsLivePlayer({ cameraId, name, active, onDimensions }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const dimensionsRef = useRef(onDimensions)
  dimensionsRef.current = onDimensions
  const [state, setState] = useState<PlayerState>('connecting')
  const [phase, setPhase] = useState('access')
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null)
  const samplesRef = useRef<Snapshot[]>([])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let disposed = false
    let player: Hls | null = null
    let accessAbort: AbortController | null = null
    let accessTimeout: number | undefined
    let retryTimer: number | undefined
    let frameCallback: number | undefined
    let generation = 0, attempts = 0, retries = 0, authRenewals = 0
    let currentState: PlayerState = active ? 'connecting' : 'stopped'
    let currentPhase = active ? 'access' : 'inactive'
    let startedAt = performance.now(), firstFrameAt = 0, lastFrameAt = 0
    let presentedFrames = 0, previousPresented = 0, previousFrames = 0
    let previousCallbackFrames: number | null = null
    let previousTime = 0, previousSampleAt = performance.now()
    let stalls = 0, stalledAt = 0, stalledMs = 0
    let retryBlocked = false
    samplesRef.current = []
    setSnapshot(null)
    if (surfaceRef.current) delete surfaceRef.current.dataset.liveMetrics

    function transition(nextState: PlayerState, nextPhase: string) {
      currentState = nextState
      currentPhase = nextPhase
      if (!disposed) { setState(nextState); setPhase(nextPhase) }
    }
    function clearAttempt() {
      generation++
      window.clearTimeout(accessTimeout)
      accessAbort?.abort()
      accessAbort = null
      player?.destroy()
      player = null
      video!.pause()
      video!.srcObject = null
      video!.removeAttribute('src')
      video!.load()
    }
    function retry(reason: string) {
      if (disposed || retryBlocked || retryTimer !== undefined) return
      transition(navigator.onLine ? 'waiting' : 'offline', reason)
      clearAttempt()
      const delay = retryDelays[Math.min(retries++, retryDelays.length - 1)]
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined
        if (navigator.onLine) void startAttempt()
        else retry('network_offline')
      }, delay)
    }
    function unavailable(reason: string) {
      retryBlocked = true
      clearAttempt()
      transition('unavailable', reason)
    }
    function frameAdvanced() {
      const now = performance.now()
      if (!firstFrameAt) firstFrameAt = now
      lastFrameAt = now
      if (stalledAt) { stalledMs += now - stalledAt; stalledAt = 0 }
      transition('playing', 'frames_advancing')
      if (now - firstFrameAt > 10000) retries = 0
    }
    async function startAttempt() {
      if (disposed || retryBlocked) return
      clearAttempt()
      const own = generation
      const isCurrent = () => !disposed && own === generation
      const controller = new AbortController()
      accessAbort = controller
      attempts++
      startedAt = performance.now()
      firstFrameAt = 0
      lastFrameAt = 0
      previousFrames = 0
      previousCallbackFrames = null
      previousTime = 0
      transition('connecting', 'access')
      accessTimeout = window.setTimeout(() => controller.abort(), 15000)
      try {
        const [access, { default: Player, FetchLoader }] = await Promise.all([
          api<LiveAccess>(`/recordings/cameras/${encodeURIComponent(cameraId)}/live-access`, { method: 'POST', signal: controller.signal }),
          import('hls.js'),
        ])
        if (!isCurrent() || controller.signal.aborted) { if (isCurrent()) retry('access_timeout'); return }
        window.clearTimeout(accessTimeout)
        if (!Player.isSupported()) { unavailable('hls_browser_unsupported'); return }
        const ticket = resolveHlsAccess(access)
        const hls = new Player({
          debug: false,
          loader: FetchLoader,
          lowLatencyMode: false,
          liveSyncDuration: 8,
          liveMaxLatencyDuration: 24,
          maxLiveSyncPlaybackRate: 1,
          maxBufferLength: 16,
          maxMaxBufferLength: 24,
          backBufferLength: 4,
          fetchSetup: async (context, init) => {
            if (!isCurrent()) throw new Error('hls_cancelled')
            const target = new URL(context.url)
            if (target.origin !== ticket.origin || !target.pathname.startsWith(ticket.prefix) ||
              target.username || target.password || target.hash) throw new Error('hls_resource_blocked')
            const headers = new Headers(init.headers)
            headers.set('Authorization', `Bearer ${ticket.token}`)
            // The loader owns the abort signal for every playlist, key and segment.
            // Reject redirects and keep tickets in memory and Authorization only.
            return new Request(target.href, { ...init, headers, credentials: 'omit', cache: 'no-store', redirect: 'error' })
          },
        })
        player = hls
        let recoveredMedia = false
        hls.on(Player.Events.ERROR, (_event, data) => {
          if (!isCurrent()) return
          if (data.response?.code === 401) { authRenewals++; retry('renewing_access'); return }
          if (data.response?.code === 403) { unavailable('access_denied'); return }
          if (!data.fatal) return
          if (data.details === Player.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR ||
            data.details === Player.ErrorDetails.BUFFER_INCOMPATIBLE_CODECS_ERROR) {
            unavailable('hls_codec_unsupported')
          } else if (data.type === Player.ErrorTypes.MEDIA_ERROR && !recoveredMedia) {
            recoveredMedia = true
            transition('waiting', 'media_recovery')
            hls.recoverMediaError()
          } else retry(data.type === Player.ErrorTypes.NETWORK_ERROR ? 'hls_network_retry' : 'hls_media_retry')
        })
        hls.on(Player.Events.MANIFEST_PARSED, () => {
          if (!isCurrent()) return
          transition('connecting', 'buffering')
          void video!.play().catch(() => { if (isCurrent()) transition('waiting', 'playback_blocked') })
        })
        hls.attachMedia(video!)
        hls.loadSource(ticket.url)
      } catch (error) {
        if (!isCurrent()) return
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) unavailable('access_denied')
        else if (error instanceof Error && error.message === 'hls_access_unavailable') unavailable('hls_access_unavailable')
        else retry('access_retry')
      } finally {
        if (isCurrent()) window.clearTimeout(accessTimeout)
      }
    }

    transition(currentState, currentPhase)
    if (!active) return () => { disposed = true; clearAttempt() }
    const supportsFrameCallback = typeof video.requestVideoFrameCallback === 'function'
    const onFrame = (_now: number, metadata: VideoFrameCallbackMetadata) => {
      if (disposed) return
      if (player) {
        // A busy main thread can skip callbacks while the compositor presents
        // several frames. Count the browser's cumulative presentations.
        presentedFrames += previousCallbackFrames === null ? 1 : Math.max(1, metadata.presentedFrames - previousCallbackFrames)
        previousCallbackFrames = metadata.presentedFrames
        frameAdvanced()
      }
      frameCallback = video.requestVideoFrameCallback(onFrame)
    }
    if (supportsFrameCallback) frameCallback = video.requestVideoFrameCallback(onFrame)
    const dimensions = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) dimensionsRef.current?.(video.videoWidth, video.videoHeight)
    }
    const online = () => {
      if (disposed || retryBlocked) return
      window.clearTimeout(retryTimer)
      retryTimer = undefined
      void startAttempt()
    }
    const offline = () => retry('network_offline')
    video.addEventListener('resize', dimensions)
    video.addEventListener('loadedmetadata', dimensions)
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    const sampleTimer = window.setInterval(() => {
      const now = performance.now()
      const surface = surfaceRef.current
      const rect = surface?.getBoundingClientRect()
      const left = Math.max(0, rect?.left ?? 0), right = Math.min(window.innerWidth, rect?.right ?? 0)
      const top = Math.max(0, rect?.top ?? 0), bottom = Math.min(window.innerHeight, rect?.bottom ?? 0)
      const covering = !document.hidden && left < right && top < bottom
        ? document.elementFromPoint((left + right) / 2, (top + bottom) / 2) : null
      const onScreen = Boolean(surface && covering && (surface.contains(covering) || covering.contains(surface)))
      const quality = video.getVideoPlaybackQuality?.()
      const frames = quality?.totalVideoFrames ?? 0
      const droppedFrames = quality?.droppedVideoFrames ?? 0
      const decodedFrames = Math.max(0, frames - droppedFrames)
      const elapsed = Math.max(1, now - previousSampleAt)
      const timeAdvanced = video.currentTime > previousTime + .02
      const framesAdvanced = decodedFrames > previousFrames
      // Occluded tiles may not receive compositor callbacks. Keep decoded and
      // presented counters separate; a hidden tile must not trigger reconnects.
      if (player && ((!supportsFrameCallback || !onScreen) && timeAdvanced && framesAdvanced)) frameAdvanced()
      const frameAge = lastFrameAt ? now - lastFrameAt : null
      if (player && onScreen && frameAge !== null && frameAge > 2500) {
        if (!stalledAt) { stalledAt = lastFrameAt; stalls++ }
        transition('waiting', 'frames_stalled')
      }
      let buffered = 0
      for (let index = 0; index < video.buffered.length; index++) {
        if (video.buffered.start(index) <= video.currentTime + .1 && video.buffered.end(index) > video.currentTime) {
          buffered = video.buffered.end(index) - video.currentTime
          break
        }
      }
      const latency = player?.latency
      const entry: Snapshot = {
        atUtc: new Date().toISOString(), source: 'hls', state: currentState, phase: currentPhase,
        attempt: attempts, reconnects: Math.max(0, attempts - 1), authRenewals,
        currentTime: rounded(video.currentTime), videoFrames: frames, framesDecoded: decodedFrames, framesDropped: droppedFrames,
        presentedFrames, fps: rounded(Math.max(0, decodedFrames - previousFrames) * 1000 / elapsed),
        presentedFps: rounded((presentedFrames - previousPresented) * 1000 / elapsed), bufferedSeconds: rounded(buffered),
        latencySeconds: latency !== undefined && Number.isFinite(latency) ? rounded(latency) : null,
        firstFrameMs: firstFrameAt ? Math.round(firstFrameAt - startedAt) : null,
        frameAgeMs: frameAge === null ? null : Math.round(frameAge), stalls,
        stalledMs: Math.round(stalledMs + (stalledAt ? now - stalledAt : 0)), readyState: video.readyState,
        paused: video.paused, visibility: document.visibilityState, onScreen,
      }
      // Fixed labels and counters only: never serialize hls.js errors, URLs or tickets.
      samplesRef.current = [...samplesRef.current.slice(-299), entry]
      if (surfaceRef.current) surfaceRef.current.dataset.liveMetrics = JSON.stringify(entry)
      setSnapshot(entry)
      previousFrames = decodedFrames
      previousPresented = presentedFrames
      previousTime = video.currentTime
      previousSampleAt = now
      if (player && onScreen && ((frameAge !== null && frameAge > 30000) || (!firstFrameAt && now - startedAt > 45000))) retry('frame_timeout')
    }, 1000)
    void startAttempt()
    return () => {
      disposed = true
      window.clearInterval(sampleTimer)
      window.clearTimeout(retryTimer)
      if (frameCallback !== undefined) video.cancelVideoFrameCallback(frameCallback)
      video.removeEventListener('resize', dimensions)
      video.removeEventListener('loadedmetadata', dimensions)
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
      clearAttempt()
      if (surfaceRef.current) delete surfaceRef.current.dataset.liveMetrics
    }
  }, [active, cameraId])

  function downloadDiagnostics() {
    const url = URL.createObjectURL(new Blob([JSON.stringify({ schema: 1, source: 'hls', samples: samplesRef.current }, null, 2)], { type: 'application/json' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'spectra-hls-diagnostico.json'
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  const message = state === 'unavailable' ? 'HLS indisponível para esta câmera ou navegador.'
    : state === 'offline' ? 'Sem conexão. Aguardando a rede…'
      : state === 'stopped' ? 'Transmissão ao vivo suspensa.'
        : state === 'waiting' ? 'Aguardando quadros da câmera…' : 'Preparando vídeo com buffer…'
  return <div ref={surfaceRef} className="stream-player" data-stream-source="hls" data-stream-state={state} data-stream-diagnostic={phase}>
    <video ref={videoRef} autoPlay muted playsInline aria-label={`Transmissão ao vivo: ${name}`} />
    {state !== 'playing' && <div className="stream-recovery" role="status"><span className="camera-pulse" />{message}</div>}
    <details style={{ position: 'absolute', top: 8, right: 8, zIndex: 4, maxWidth: '95%', maxHeight: '80%', overflow: 'auto', padding: 8, color: '#fff', background: '#071b2ef2', fontSize: 11 }} onClick={event => event.stopPropagation()}>
      <summary>Diagnóstico HLS</summary>
      <button type="button" onClick={downloadDiagnostics}>Baixar diagnóstico JSON</button>
      <pre aria-label={`Diagnóstico HLS de ${name}`} style={{ whiteSpace: 'pre-wrap', maxWidth: 350 }}>{JSON.stringify(snapshot, null, 2)}</pre>
    </details>
  </div>
}
